/**
 * Canonical glossary term registry (Section 5.11 initial taxonomy).
 *
 * Every user-visible metric, framework, model, signal, status, score, action,
 * data-quality term, and chart transformation references one of these term IDs.
 * The coverage checker fails CI when a registered term lacks a valid entry.
 */

export interface GlossaryTermRef {
  termId: string;
  slug: string;
  term: string;
  category: GlossaryCategory;
  aliases: string[];
  usedOnRoutes: string[];
}

export type GlossaryCategory =
  | 'macro'
  | 'risk'
  | 'commodities'
  | 'fx'
  | 'market_structure'
  | 'geopolitics'
  | 'tps'
  | 'valuation'
  | 'portfolio'
  | 'learning'
  | 'provenance';

const T = (
  category: GlossaryCategory,
  slug: string,
  term: string,
  aliases: string[] = [],
  usedOnRoutes: string[] = [],
): GlossaryTermRef => ({ termId: `glossary.${slug}`, slug, term, category, aliases, usedOnRoutes });

export const GLOSSARY_TERMS: readonly GlossaryTermRef[] = [
  // macro
  T('macro', 'fed-funds-rate', 'Fed Funds Rate', ['FFR', 'policy rate'], ['/markets', '/command']),
  T('macro', 'breakeven-inflation', 'Breakeven Inflation', ['BEI', 'inflation breakeven'], ['/markets']),
  T('macro', 'real-policy-rate', 'Real Policy Rate', ['real rate', 'real fed funds'], ['/markets', '/command']),
  T('macro', 'gdp-trend', 'GDP Trend', ['real GDP', 'output trend'], ['/markets']),
  T('macro', 'unemployment-rate', 'Unemployment Rate', ['U3', 'jobless rate'], ['/markets']),
  T('macro', 'yield-curve-10y-2y', '10Y–2Y Yield Curve', ['curve inversion', '2s10s'], ['/markets', '/command']),
  T('macro', 'term-premium', 'Term Premium', [], ['/markets']),
  T('macro', 'liquidity-conditions', 'Liquidity Conditions', ['net liquidity'], ['/markets']),
  // risk
  T('risk', 'vix', 'VIX', ['volatility index', 'fear gauge'], ['/markets', '/command']),
  T('risk', 'spot-versus-etf', 'Spot Versus ETF Price', [], ['/markets']),
  T('risk', 'gold-signal', 'Gold as Signal', ['GLD signal'], ['/markets', '/command']),
  T('risk', 'oil-signal', 'Oil as Signal', ['Brent signal', 'USO'], ['/markets', '/command']),
  T('risk', 'ten-year-yield', '10-Year Treasury Yield', ['10Y yield', 'UST10Y'], ['/markets', '/command']),
  T('risk', 'dollar-index-proxy', 'Dollar Proxy (UUP)', ['UUP', 'dollar strength'], ['/markets']),
  T('risk', 'defense-proxy', 'Defense Proxy (ITA)', ['ITA', 'defense sector proxy'], ['/markets']),
  T('risk', 'managed-conflict-vs-crisis', 'Managed Conflict vs Real Crisis', ['conflict classifier'], ['/markets', '/command', '/world']),
  // commodities
  T('commodities', 'energy-basket', 'Energy Bucket', ['energy commodities'], ['/markets']),
  T('commodities', 'growth-metals', 'Growth Metals Bucket', ['copper bucket', 'industrial metals'], ['/markets']),
  T('commodities', 'agriculture-basket', 'Agriculture Bucket', ['ags', 'softs'], ['/markets']),
  T('commodities', 'precious-metals', 'Precious Metals Bucket', ['gold silver basket'], ['/markets']),
  T('commodities', 'broad-commodity-baskets', 'Broad Commodity Baskets', ['DBC limitation'], ['/markets']),
  T('commodities', 'inventory-and-curve-structure', 'Inventory and Curve Structure', ['contango', 'backwardation'], ['/markets']),
  // fx
  T('fx', 'reserve-currency', 'Reserve Currency', ['USD reserve'], ['/markets', '/world']),
  T('fx', 'managed-currency', 'Managed Currency', ['managed exchange rate'], ['/markets', '/world']),
  T('fx', 'fix-versus-spot', 'Currency Fix Versus Spot', ['CNY fix', 'fix versus spot'], ['/markets']),
  T('fx', 'commodity-currency', 'Commodity Currency', ['terms-of-trade FX'], ['/markets']),
  T('fx', 'crisis-currency-pattern', 'Crisis Currency Pattern', ['EM FX stress'], ['/markets']),
  T('fx', 'carry-and-stress', 'Carry and Stress Patterns', ['carry trade unwind'], ['/markets']),
  // market structure
  T('market_structure', 'market-breadth', 'Market Breadth', ['advance decline', 'participation'], ['/markets']),
  T('market_structure', 'sector-rotation', 'Sector Rotation', ['1M 5D 1D rotation'], ['/markets']),
  T('market_structure', 'passive-flow', 'Passive Flow', ['index flow', 'ETF flow premium'], ['/markets', '/capital']),
  T('market_structure', 'ownership-concentration', 'Ownership Concentration', ['holder concentration'], ['/capital', '/securities']),
  T('market_structure', 'insider-activity', 'Insider Activity', ['insider buying', 'Form 4'], ['/securities', '/markets']),
  T('market_structure', 'short-interest', 'Short Interest', ['SI', 'days to cover'], ['/securities']),
  T('market_structure', 'correlation-and-factor-exposure', 'Correlation and Factor Exposure', ['factor loading'], ['/portfolio']),
  // geopolitics
  T('geopolitics', 'principals', 'Principals', ['players', 'actors'], ['/world']),
  T('geopolitics', 'cannot-lose-line', 'Cannot-Lose Line', ['red line substance'], ['/world']),
  T('geopolitics', 'hidden-constraint', 'Hidden Constraint', [], ['/world']),
  T('geopolitics', 'leverage', 'Leverage', ['bargaining leverage'], ['/world']),
  T('geopolitics', 'dominant-strategy', 'Dominant Strategy', [], ['/world']),
  T('geopolitics', 'nash-destination', 'Nash Destination', ['equilibrium destination'], ['/world']),
  T('geopolitics', 'finite-infinite-game', 'Finite vs Infinite Game', ['game type'], ['/world']),
  T('geopolitics', 'wrapper-vs-substance', 'Wrapper vs Substance', ['headline vs carve-out'], ['/world']),
  T('geopolitics', 'carve-out-is-the-policy', 'The Carve-Out Is the Policy', ['carve-out'], ['/world']),
  T('geopolitics', 'lease-vs-deed', 'Lease vs Deed', ['temporary access versus ownership'], ['/world']),
  T('geopolitics', 'kill-zone', 'Kill Zone', ['contested exclusion zone'], ['/world']),
  T('geopolitics', 'toll-gate-state', 'Toll-Gate State', ['price of admission'], ['/world']),
  T('geopolitics', 'patient-buyer', 'Patient Buyer', [], ['/world', '/markets']),
  T('geopolitics', 'managed-conflict', 'Managed Conflict', ['managed escalation'], ['/world', '/markets']),
  T('geopolitics', 'junior-partner', 'Junior Partner Positioning', ['position inside the system'], ['/world']),
  T('geopolitics', 'costly-signals', 'Costly Signals', ['expensive signals'], ['/world', '/command']),
  // tps
  T('tps', 'tps-factions', 'TPS Factions (MIC/FIC/TIC/CIC)', ['transnational private sector'], ['/capital']),
  T('tps', 'host-migration', 'Host Migration', ['revenue migration'], ['/world', '/capital']),
  T('tps', 'immunity-dimensions', 'Immunity Dimensions', ['structural immunity'], ['/capital', '/securities']),
  T('tps', 'rented-immunity', 'Rented Immunity', [], ['/capital']),
  T('tps', 'rail-vs-product', 'Rail vs Product', ['rails and products'], ['/capital']),
  T('tps', 'protected-node-disappearance-test', 'Protected-Node Disappearance Test', ['disappearance test'], ['/capital', '/securities']),
  T('tps', 'incentive-architecture', 'Incentive Architecture', ['ownership incentives'], ['/capital']),
  T('tps', 'structural-protection-score', 'Structural Protection Score', ['protection score'], ['/capital', '/securities']),
  // valuation
  T('valuation', 'dcf', 'Discounted Cash Flow (DCF)', ['three-stage DCF'], ['/securities']),
  T('valuation', 'discount-rate', 'Discount Rate', ['WACC', 'required return'], ['/securities']),
  T('valuation', 'terminal-value', 'Terminal Value', ['TV share'], ['/securities']),
  T('valuation', 'epv', 'Earnings Power Value (EPV)', ['no-growth value'], ['/securities']),
  T('valuation', 'ebo', 'EBO Residual Income', ['Edwards-Bell-Ohlson'], ['/securities']),
  T('valuation', 'ev-ebitda', 'EV/EBITDA', ['enterprise multiple'], ['/securities']),
  T('valuation', 'forward-pe', 'Forward P/E', ['forward multiple'], ['/securities']),
  T('valuation', 'ddm', 'Dividend Discount Model (DDM)', ['Gordon growth'], ['/securities']),
  T('valuation', 'reverse-dcf', 'Reverse DCF', ['implied expectations'], ['/securities']),
  T('valuation', 'monte-carlo-valuation', 'Monte Carlo Valuation', ['valuation distribution'], ['/securities']),
  T('valuation', 'spm-dcf', 'SPM-DCF', ['structural power model DCF'], ['/securities']),
  T('valuation', 'flow-premium-clawback', 'Flow Premium and Clawback', ['passive premium clawback'], ['/securities', '/capital']),
  // portfolio
  T('portfolio', 'cagr', 'CAGR', ['compound annual growth rate'], ['/portfolio', '/learn']),
  T('portfolio', 'real-return', 'Real Return', ['inflation-adjusted return'], ['/portfolio']),
  T('portfolio', 'volatility', 'Volatility', ['standard deviation of returns'], ['/portfolio', '/learn']),
  T('portfolio', 'max-drawdown', 'Maximum Drawdown', ['drawdown'], ['/portfolio', '/learn']),
  T('portfolio', 'sortino-ratio', 'Sortino Ratio', ['downside deviation'], ['/portfolio', '/learn']),
  T('portfolio', 'beta', 'Beta', ['market beta'], ['/portfolio']),
  T('portfolio', 'liquidity-floor', 'Liquidity Floor', ['liquidity constraint'], ['/portfolio']),
  T('portfolio', 'concentration-risk', 'Concentration Risk', ['position concentration'], ['/portfolio']),
  T('portfolio', 'risk-budget', 'Risk Budget', ['loss budget'], ['/portfolio']),
  T('portfolio', 'probability-of-ruin', 'Probability of Ruin', ['ruin constraint'], ['/portfolio']),
  T('portfolio', 'emergency-reserve', 'Emergency Reserve', ['survival buffer'], ['/portfolio']),
  T('portfolio', 'spending-multiple', 'Spending Multiple (25×)', ['retirement multiple'], ['/portfolio']),
  T('portfolio', 'thesis-correlation', 'Thesis Correlation', ['hidden concentration'], ['/portfolio']),
  T('portfolio', 'no-action-validity', 'No-Action Validity', ['cash as optionality'], ['/command', '/portfolio', '/theses']),
  // learning
  T('learning', 'counterfactual', 'Counterfactual', ['rejected candidate tracking'], ['/learn']),
  T('learning', 'rejected-candidate', 'Rejected Candidate', [], ['/learn']),
  T('learning', 'proxy-live-separation', 'Proxy vs Live Separation', ['proxy outcomes'], ['/learn']),
  T('learning', 'decision-value-r', 'Decision Value R', ['gate value r generalization'], ['/learn']),
  T('learning', 'brier-score', 'Brier Score', ['forecast accuracy'], ['/learn']),
  T('learning', 'calibration', 'Calibration', ['forecast calibration'], ['/learn']),
  T('learning', 'holdout', 'Holdout', ['train holdout split'], ['/learn']),
  T('learning', 'shadow-policy', 'Shadow Policy', ['shadow mode'], ['/learn']),
  T('learning', 'canary', 'Canary', ['canary release'], ['/learn']),
  T('learning', 'fdr', 'False Discovery Rate (FDR)', ['multiple comparisons'], ['/learn']),
  T('learning', 'clustered-sample', 'Clustered Sample', ['effective sample size'], ['/learn']),
  // provenance
  T('provenance', 'valid-at', 'Valid-At', ['effective time'], ['/operations', '/library']),
  T('provenance', 'known-at', 'Known-At', ['knowledge time'], ['/operations', '/library']),
  T('provenance', 'ingested-at', 'Ingested-At', ['storage time'], ['/operations']),
  T('provenance', 'vintage', 'Vintage', ['release vintage'], ['/operations', '/markets']),
  T('provenance', 'revision', 'Revision', ['data revision'], ['/operations']),
  T('provenance', 'freshness-sla', 'Freshness SLA', ['staleness policy'], ['/operations', '/command']),
  T('provenance', 'trust-tier', 'Trust Tier', ['source hierarchy'], ['/library', '/operations']),
  T('provenance', 'partial-state', 'Partial State', ['partial data'], ['/command', '/operations']),
  T('provenance', 'unavailable-state', 'Unavailable State', ['no fabricated data'], ['/command', '/operations']),
  T('provenance', 'contradicted-state', 'Contradicted State', ['conflicting sources'], ['/operations']),
] as const;

const byId = new Map(GLOSSARY_TERMS.map((t) => [t.termId, t]));
const bySlug = new Map(GLOSSARY_TERMS.map((t) => [t.slug, t]));
const aliasIndex = new Map<string, GlossaryTermRef>();
for (const t of GLOSSARY_TERMS) {
  aliasIndex.set(t.term.toLowerCase(), t);
  for (const a of t.aliases) aliasIndex.set(a.toLowerCase(), t);
}

export function getTerm(termId: string): GlossaryTermRef | undefined {
  return byId.get(termId);
}

export function getTermBySlug(slug: string): GlossaryTermRef | undefined {
  return bySlug.get(slug);
}

export function resolveAlias(query: string): GlossaryTermRef | undefined {
  return aliasIndex.get(query.trim().toLowerCase());
}

export function searchTerms(query: string): GlossaryTermRef[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return GLOSSARY_TERMS.filter(
    (t) =>
      t.term.toLowerCase().includes(q) ||
      t.slug.includes(q) ||
      t.aliases.some((a) => a.toLowerCase().includes(q)),
  );
}

export function isRegisteredTermId(termId: string): boolean {
  return byId.has(termId);
}
